/**
 * 組織レポート v3「つながりの構造」の判定（仕様書§06 / 説明文DB DB05）。
 *
 * 正典: apps/web/docs/新方針20260821/GB組織_説明文DB_v1_23.json の DB05
 *
 * 【仕様書§06との違い（重要）】
 *  仕様書§06は固定閾値（VV 60%・V 40%）で書かれているが、DB05 の
 *  05-RULE-固定閾値（2026-08-20確定）がこれを明確に否定している。
 *  無作為に指名した場合の最大被指名率は人数に強く依存し、7名では中央値57%、
 *  20名では25%になる。固定40%だと7名の部署はほぼ全部がV、20名の部署では
 *  VVがまず出ない。よって人数別の帰無基準（05-基準表）を採用する。
 *
 * 【帰無基準の検算】
 *  「各人が自部署の他メンバーから1〜3名を無作為に挙げる」モデルで自前に
 *  シミュレーションしたところ、95%点(V)は 7/9/12/16/20/30名すべてDB記載値と一致した。
 *  99%点(VV)も n=9 以上は一致。n=7 のみ自前71%・DB86%で差が出る（n=7では
 *  最大被指名数が5人→71%、6人→86%と離散的で、99%点がちょうど境目に来るため）。
 *  ここではDBの値を正とする。
 *
 * 【匿名化は行わない（2026-08-22 指示）】
 *  DB05 の 05-最小 は「3名以下の部署は氏名を伏せる」としているが、氏名は常に表示する。
 *  前バージョンでも相談ネットワークは実名表示になっており、閲覧者は経営者・管理者に
 *  限定されている。よって 05-最小 / 05-推測可能 の文言は使わない。
 *  人数による制限は「7名未満は判定しない」だけで、これは統計上の下限。
 *
 * 【判定の順序（DB05 判定入力）】
 *  ①分断 → ②VV(一極集中) → ③V(集まりぎみ) → ④△(相互指名の3人組) → ⑤希薄
 *  VV・Vが先に立つのは、集中している部署に三角があってもリスクを先に伝えるため。
 */

/** 1人分。 */
export interface ConsultPerson {
  id: string;
  /** 表示名。人数が少ない部署では伏せる（呼び出し側で判断） */
  name: string;
}

/** 有向の相談エッジ（from が to を挙げた）。 */
export interface ConsultEdgeV3 {
  from: string;
  to: string;
}

export type ConsultShapeV3 = '分断' | '一極集中' | '集まりぎみ' | '助け合い' | '希薄';

/** 記号と色（モックv3の凡例に合わせる）。 */
export const CONSULT_SHAPE_META: Record<
  ConsultShapeV3,
  { mark: string; label: string; color: string; tone: 'red' | 'amber' | 'green' | 'gray' }
> = {
  分断: { mark: '‖', label: '島が複数に分かれている', color: '#B03A2E', tone: 'red' },
  一極集中: { mark: 'VV', label: '1人に集中', color: '#B03A2E', tone: 'red' },
  集まりぎみ: { mark: 'V', label: '1人に集まりぎみ', color: '#B8860B', tone: 'amber' },
  助け合い: { mark: '△', label: '助け合えている', color: '#2E7D46', tone: 'green' },
  希薄: { mark: '—', label: '連携が限定的', color: '#8A94A2', tone: 'gray' },
};

/**
 * 人数別の帰無基準（05-基準表・2026-08-20確定）。
 * [回答者数, V=95%点, VV=99%点]。中間の人数は線形補間する。
 */
export const CONSULT_NULL_TABLE: ReadonlyArray<readonly [number, number, number]> = [
  // 4〜6名はDBに記載が無いため自前のシミュレーション（20万試行）で補った。
  // 少人数では被指名数が離散的で、95%点と99%点が同じ値に重なる。
  // つまり V と VV を区別できない（6名なら「6人中5人」が唯一の判定線）。
  // 6名83% → 7名71% と非単調になるのも離散性によるもので、誤りではない。
  [4, 0.75, 0.75],
  [5, 0.8, 0.8],
  [6, 0.83, 0.83],
  [7, 0.71, 0.86],
  [9, 0.56, 0.67],
  [12, 0.5, 0.58],
  [16, 0.38, 0.44],
  [20, 0.3, 0.35],
  [30, 0.23, 0.27],
];

/**
 * 判定を行う最小人数（これ未満は観測値のみ）。統計上の下限であって匿名化ではない。
 *
 * 【なぜ7名か】無作為に指名した場合でも相互指名の3人組は
 *   3名 29% / 4名 26% / 5名 10% / 6名 4.5% / 7名 2.3% / 9名 0.9%
 * の確率でできてしまう（20万試行）。DB05 記載の 7名3%・9名1%・12名以上0% と一致する。
 * 3〜4名では3〜4部署に1つが偶然で「助け合えている」と表示されることになる。
 * 6名の4.5%は統計で慣習的に使う5%水準とほぼ同じなので、下限を6名にするのは許容できる。
 */
export const CONSULT_MIN_JUDGE = 7;
/** 平均指名数がこれ未満なら希薄（05-希薄基準・暫定）。 */
export const SPARSE_MAX_NOMINATION = 1.2;

/** 人数から帰無基準を引く。表の範囲外は端の値で頭打ち。 */
export function getConsultNullLevels(n: number): { v95: number; vv99: number } {
  const t = CONSULT_NULL_TABLE;
  if (n <= t[0][0]) return { v95: t[0][1], vv99: t[0][2] };
  const last = t[t.length - 1];
  if (n >= last[0]) return { v95: last[1], vv99: last[2] };
  for (let i = 1; i < t.length; i++) {
    if (n <= t[i][0]) {
      const lo = t[i - 1];
      const hi = t[i];
      const w = (n - lo[0]) / (hi[0] - lo[0]);
      return { v95: lo[1] + (hi[1] - lo[1]) * w, vv99: lo[2] + (hi[2] - lo[2]) * w };
    }
  }
  return { v95: last[1], vv99: last[2] };
}

/**
 * 「集中しています」と断定できるかの追加条件（05-判定基準）。
 * r ＝ 最大被指名数 ÷ 平均被指名数 が、人数帯ごとの偶然基準以上であること。
 */
export function concentrationAssertThreshold(n: number): number {
  if (n < 7) return Infinity;
  if (n <= 11) return 2.7;
  if (n <= 19) return 2.9;
  return 3.3;
}

export interface ConsultDeptResultV3 {
  dept: string;
  /** 回答者数 */
  respondents: number;
  /** 形状。人数が足りず判定しない場合は null */
  shape: ConsultShapeV3 | null;
  /** 最も多く挙げられた人（同数なら先頭） */
  topId: string | null;
  topName: string | null;
  /** その人の被指名数 */
  maxInDegree: number;
  /** 最大被指名率 = 最大被指名数 ÷ 回答者数（表示用） */
  maxRate: number;
  /** 平均指名数 = 総指名数 ÷ 回答者数（0〜3） */
  avgNomination: number;
  /** r = 最大被指名数 ÷ 平均被指名数 */
  ratio: number;
  /** 「集中しています」と断定してよいか */
  canAssertConcentration: boolean;
  /** 相互に指名し合う3人組があるか */
  hasTriangle: boolean;
  /** その3人組のID（図で強調するため）。無ければ null */
  triangleIds: [string, string, string] | null;
  /** 双方向に指名し合っている組 */
  mutualPairs: Array<[string, string]>;
  /** つながりの島の数 */
  islands: number;
  /** 被指名0かつ指名0の人がいるか（副次ラベル） */
  hasIsolated: boolean;
  /** 判定に足りる人数か（7名以上） */
  judged: boolean;
  inDegree: Record<string, number>;
  members: ConsultPerson[];
  edges: ConsultEdgeV3[];
}

/**
 * つながりの島の数（無向グラフとみなした連結成分）。
 *
 * 【孤立者は数えない】1本も線を持たない人まで島として数えると、回答していない人が
 * いるだけで必ず「島が2つ以上」になり、どの部署も分断と判定されてしまう。
 * DB05 でも孤立は形状ではなく副次ラベルとして別に扱っている。
 * よって、線を1本以上持つ人だけで連結成分を数える。
 */
function countIslands(members: ConsultPerson[], edges: ConsultEdgeV3[]): number {
  const linked = new Set<string>();
  edges.forEach((e) => {
    linked.add(e.from);
    linked.add(e.to);
  });
  members = members.filter((m) => linked.has(m.id));
  if (members.length === 0) return 0;
  const idx = new Map(members.map((m, i) => [m.id, i]));
  const parent = members.map((_, i) => i);
  const find = (a: number): number => (parent[a] === a ? a : (parent[a] = find(parent[a])));
  edges.forEach((e) => {
    const a = idx.get(e.from);
    const b = idx.get(e.to);
    if (a == null || b == null) return;
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  });
  return new Set(members.map((_, i) => find(i))).size;
}

/** 双方向に指名し合っている組（id1 < id2 の順で1件ずつ）。 */
export function mutualPairsOf(edges: ConsultEdgeV3[]): Array<[string, string]> {
  const has = new Set(edges.map((e) => `${e.from}>${e.to}`));
  const seen = new Set<string>();
  const out: Array<[string, string]> = [];
  edges.forEach((e) => {
    if (!has.has(`${e.to}>${e.from}`)) return;
    const key = [e.from, e.to].sort().join('|');
    if (seen.has(key)) return;
    seen.add(key);
    out.push([e.from, e.to]);
  });
  return out;
}

/**
 * 相互に指名し合う3人組を探し、見つかればその3名のIDを返す。
 *
 * 【なぜ三角形を探すのか】
 *  相談が一方向に集まる形（V字）は、その人が抜けると経路が切れる。
 *  互いに相談し合う3人組があれば、1人が不在でも残り2人で支え合える。
 *  無作為に指名した場合に三角が偶然できる確率は 7名3%・9名1%・12名以上0%で、
 *  三角があれば偶然ではないと言える（05-RULE-三角）。
 */
function findMutualTriangle(
  members: ConsultPerson[],
  edges: ConsultEdgeV3[]
): [string, string, string] | null {
  const mutual = new Map<string, Set<string>>();
  mutualPairsOf(edges).forEach(([a, b]) => {
    if (!mutual.has(a)) mutual.set(a, new Set());
    if (!mutual.has(b)) mutual.set(b, new Set());
    mutual.get(a)!.add(b);
    mutual.get(b)!.add(a);
  });
  const ids = members.map((m) => m.id);
  for (let i = 0; i < ids.length; i++) {
    for (let j = i + 1; j < ids.length; j++) {
      if (!mutual.get(ids[i])?.has(ids[j])) continue;
      for (let k = j + 1; k < ids.length; k++) {
        if (mutual.get(ids[i])?.has(ids[k]) && mutual.get(ids[j])?.has(ids[k])) {
          return [ids[i], ids[j], ids[k]];
        }
      }
    }
  }
  return null;
}

/** 1部署分を判定する。 */
export function judgeConsultDeptV3(
  dept: string,
  members: ConsultPerson[],
  edges: ConsultEdgeV3[],
  /** 判定を行う最小人数。既定は CONSULT_MIN_JUDGE（7名） */
  minJudge: number = CONSULT_MIN_JUDGE
): ConsultDeptResultV3 {
  const n = members.length;
  const inDegree: Record<string, number> = {};
  const outDegree: Record<string, number> = {};
  members.forEach((m) => {
    inDegree[m.id] = 0;
    outDegree[m.id] = 0;
  });
  edges.forEach((e) => {
    if (inDegree[e.to] != null) inDegree[e.to] += 1;
    if (outDegree[e.from] != null) outDegree[e.from] += 1;
  });

  let topId: string | null = null;
  let maxInDegree = 0;
  members.forEach((m) => {
    if (inDegree[m.id] > maxInDegree) {
      maxInDegree = inDegree[m.id];
      topId = m.id;
    }
  });
  const top = members.find((m) => m.id === topId) ?? null;

  const maxRate = n > 0 ? maxInDegree / n : 0;
  const avgNomination = n > 0 ? edges.length / n : 0;
  const meanInDegree = n > 0 ? edges.length / n : 0;
  const ratio = meanInDegree > 0 ? maxInDegree / meanInDegree : 0;

  const islands = countIslands(members, edges);
  const triangleIds = findMutualTriangle(members, edges);
  const hasTriangle = triangleIds !== null;
  const mutualPairs = mutualPairsOf(edges);
  const hasIsolated = members.some((m) => inDegree[m.id] === 0 && outDegree[m.id] === 0);

  const judged = n >= minJudge;
  const lv = getConsultNullLevels(n);
  // DBの基準値は整数％に丸められている（n=9のVVは67%）。実測は 6/9＝66.67% のように
  // 丸めの内側に入ることがあるため、半％ぶんの許容を置く。
  // これが無いと「モックの製造部（9名中6名）がVVにならない」といった取りこぼしが出る。
  const EPS = 0.005;

  // 判定順: ①分断 → ②VV → ③V → ④△ → ⑤希薄（DB05 判定入力）
  let shape: ConsultShapeV3 | null = null;
  if (judged) {
    if (islands >= 2) shape = '分断';
    else if (maxRate >= lv.vv99 - EPS) shape = '一極集中';
    else if (maxRate >= lv.v95 - EPS) shape = '集まりぎみ';
    else if (hasTriangle) shape = '助け合い';
    else shape = '希薄';
  }

  return {
    dept,
    respondents: n,
    shape,
    topId,
    topName: top?.name ?? null,
    maxInDegree,
    maxRate,
    avgNomination,
    ratio,
    canAssertConcentration: judged && ratio >= concentrationAssertThreshold(n),
    hasTriangle,
    triangleIds,
    mutualPairs,
    islands,
    hasIsolated,
    judged,
    inDegree,
    members,
    edges,
  };
}

/**
 * 部署ごとの説明文。
 * DB05 の本文はプレースホルダを含むため、判定結果と観測値から組み立てる。
 * 「集中しています」と断定するのは canAssertConcentration が立つときだけ（05-判定基準）。
 */
export function consultDeptSummary(stat: ConsultDeptResultV3): string {
  const top = stat.topName ? `${stat.topName}さん` : 'ある方';
  if (!stat.judged) {
    return `${stat.dept}は回答${stat.respondents}名中${stat.maxInDegree}名が${top}を挙げています。人数が少ないため、集中しているかどうかの判定は行っていません。数字としてご覧ください。`;
  }
  switch (stat.shape) {
    case '分断':
      return `${stat.dept}は相談のつながりが${stat.islands}つの島に分かれています。島をまたぐ相談が起きにくく、同じ部署でも情報が片方にしか届かない状態になりやすい傾向があります。`;
    case '一極集中':
      return stat.canAssertConcentration
        ? `回答${stat.respondents}名中${stat.maxInDegree}名が${top}を挙げています。${top}個人の問題ではなく、判断の経路が1本しかない組織構造の問題です。この状態だと、${top}が不在のときに仕事が止まりやすくなります。`
        : `回答${stat.respondents}名中${stat.maxInDegree}名が${top}を挙げています。同じ方に寄っている可能性がありますが、断定できる水準ではありません。`;
    case '集まりぎみ':
      return `回答${stat.respondents}名中${stat.maxInDegree}名が${top}を挙げています。まだ余地はありますが、同じ方に寄り始めています。今のうちにもう1人、相談を受けられる方を決めておくと安定します。`;
    case '助け合い':
      return `お互いに相談し合える関係ができています。誰か1人が休んでも、仕事が止まりにくい状態です。`;
    default:
      return `相談のやりとりが限られています。平均して1人あたり${stat.avgNomination.toFixed(1)}名しか挙げていません。困ったときに相談できる相手が決まっていない可能性があります。`;
  }
}
